import React, {} from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

import { Label } from '../ui/label';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import ConventionDownload from '../ConventionDownload';

import ConventionFilters from '../ConventionFilters';

import { FileText, Plus, Send, Eye, Trash2, CheckCircle, Clock, PauseCircle, PlayCircle, Ban, Calendar, Shield, Award, Loader2, FileSignature, Copy, CreditCard, Share2 } from 'lucide-react';
import Modals from './Modals';
import ExportImportBar from '../ExportImportBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
import useGestionConventions from './useGestionConventions';
import { ConventionHeroKpi, ConventionCellStat } from './ConventionsUI';
import HistoriquePartages from './HistoriquePartages';

export default function GestionConventions() {
  const ctx = useGestionConventions();
  const {
    _activeTab, _handleWordImport, _setActiveTab, _setSearchQuery, _setStatutFilter, _setTypeFilter,
    _setUseAdvancedFilters, actionData, actionMasseData, conventions, coopersList, creatingRecipient,
    editFormData, envoiData, expertsList, fetchData, filteredConventions, filteredConventionsList,
    formData, getAuthHeader, getFilteredContrats, getStatutBadge, getTypeIcon, handleAction,
    handleActionMasse, handleCancelEdit, handleCreateConvention, handleCreateRecipientByEmail, handleDelete, handleDuplicateConvention,
    handleEnvoyerConvention, handleOpenEdit, handleSaveEdit, handleSelectAll, handleSelectConvention, isEditing,
    loading, newRecipientEmail, newRecipientEntreprise, newRecipientNom, newRecipientPrenom, openActionMasseModal,
    openActionModal, openCreateModal, openDetailModal, openEditModal, openEnvoiModal, openPaymentShareModal,
    resetActionData, resetActionMasseData, resetEnvoiData, resetForm, searchQuery, selectAll,
    selectedConvention, selectedConventions, setActionData, setActionMasseData, setContrats, setCoopersList,
    setCreatingRecipient, setEditFormData, setEnvoiData, setExpertsList, setFilteredConventionsList, setFormData,
    setIsEditing, setLoading, setNewRecipientEmail, setNewRecipientEntreprise, setNewRecipientNom, setNewRecipientPrenom,
    setOpenActionMasseModal, setOpenActionModal, setOpenCreateModal, setOpenDetailModal, setOpenEditModal, setOpenEnvoiModal,
    setOpenPaymentShareModal, setSelectAll, setSelectedContrat, setSelectedConventions, setShowCreateRecipient, setStats,
    showCreateRecipient, stats, statutFilter, typeFilter, useAdvancedFilters} = ctx;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {/* HERO institutionnel — Conventions de Partenariat */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-6 text-white shadow-xl">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-yellow-400/10 blur-2xl" />
        <div className="absolute -left-10 bottom-0 w-56 h-56 rounded-full bg-emerald-400/10 blur-2xl" />
        <div className="relative flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
          <div className="flex items-start gap-4 flex-1">
            <div className="p-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 shrink-0">
              <FileSignature className="w-8 h-8 text-yellow-300" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-bold uppercase tracking-widest bg-yellow-400/20 text-yellow-200 px-2 py-0.5 rounded-full border border-yellow-300/30">
                  Cadre juridique &amp; institutionnel
                </span>
                <Shield className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <h2 className="text-2xl xl:text-3xl font-bold">Conventions de Partenariat</h2>
              <p className="text-sm text-blue-100 mt-1 max-w-2xl">
                Créez, signez électroniquement et pilotez les conventions cadres qui lient O&apos;SCOP Outremer à ses
                <strong className="text-yellow-200"> COOPER&apos;S</strong>,
                <strong className="text-purple-200"> EXPERTS</strong> et
                <strong className="text-emerald-200"> autres Partenaires institutionnels</strong>.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <ExportImportBar collection="conventions" label="Conventions" onImported={() => window.location.reload()} />
          </div>
        </div>
        {stats && (
          <div className="relative mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <ConventionHeroKpi label="COOPER'S actifs" value={stats.cooper.actifs} icon={Shield} accent="from-emerald-500/30 to-teal-600/30" />
            <ConventionHeroKpi label="EXPERTS actifs" value={stats.expert.actifs} icon={Award} accent="from-purple-500/30 to-fuchsia-600/30" />
            <ConventionHeroKpi label="En attente signature" value={stats.cooper.en_attente + stats.expert.en_attente} icon={Clock} accent="from-amber-500/30 to-orange-600/30" />
            <ConventionHeroKpi label="Suspendues" value={stats.cooper.suspendus + stats.expert.suspendus} icon={Ban} accent="from-rose-500/30 to-pink-600/30" />
          </div>
        )}
      </div>

      {/* Bandeau pédagogique */}
      <div className="rounded-xl border border-blue-200 bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 px-4 py-3 flex items-start gap-3 text-sm">
        <FileText className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <div className="text-slate-700 leading-relaxed">
          <strong className="text-slate-900">Cycle de vie d&apos;une convention :</strong>
          <span className="ml-1">Brouillon → Envoi pour signature → Signée → Active.</span>
          <span className="block mt-1 text-xs text-slate-600">
            Chaque convention est horodatée, signée électroniquement (RGPD-compliant) et classée automatiquement dans la GED universelle.
          </span>
        </div>
      </div>

      {/* Statistiques détaillées */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100">
              <CardTitle className="flex items-center gap-2 text-emerald-900">
                <Shield className="h-5 w-5" />Conventions COOPER&apos;S
              </CardTitle>
              <CardDescription className="text-emerald-700/80 text-xs">Mandats commerciaux des partenaires distributeurs</CardDescription>
            </CardHeader>
            <CardContent className="pt-5">
              <div className="grid grid-cols-4 gap-3">
                <ConventionCellStat color="emerald" value={stats.cooper.actifs} label="Actifs" />
                <ConventionCellStat color="amber" value={stats.cooper.en_attente} label="En attente" />
                <ConventionCellStat color="rose" value={stats.cooper.suspendus} label="Suspendus" />
                <ConventionCellStat color="slate" value={stats.cooper.total} label="Total" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200 shadow-sm overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-purple-50 to-fuchsia-50 border-b border-purple-100">
              <CardTitle className="flex items-center gap-2 text-purple-900">
                <Award className="h-5 w-5" />Conventions EXPERTS
              </CardTitle>
              <CardDescription className="text-purple-700/80 text-xs">Accompagnement technique &amp; juridique des coopérateurs</CardDescription>
            </CardHeader>
            <CardContent className="pt-5">
              <div className="grid grid-cols-4 gap-3">
                <ConventionCellStat color="emerald" value={stats.expert.actifs} label="Actifs" />
                <ConventionCellStat color="amber" value={stats.expert.en_attente} label="En attente" />
                <ConventionCellStat color="rose" value={stats.expert.suspendus} label="Suspendus" />
                <ConventionCellStat color="slate" value={stats.expert.total} label="Total" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres avancés */}
      <ConventionFilters
        conventions={conventions}
        onFilter={(filtered) => setFilteredConventionsList(filtered)}
        coopersList={coopersList}
        expertsList={expertsList}
      />

      {/* Bouton créer + résumé */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          <strong>{useAdvancedFilters ? filteredConventionsList.length : filteredConventions.length}</strong> convention(s) trouvé(s)
        </div>
        <Button
          onClick={() => setOpenCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau Convention
        </Button>
      </div>

      {/* Barre d'actions en masse */}
      {selectedConventions.length > 0 && (
        <Card className="bg-blue-50 border-2 border-blue-300">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <span className="font-semibold text-blue-900">
                  {selectedConventions.length} convention(s) sélectionné(s)
                </span>
              </div>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedConventions([]);
                    setSelectAll(false);
                  }}
                >
                  Annuler la sélection
                </Button>
                
                <Select
                  value={actionMasseData.action}
                  onValueChange={(value) => {
                    setActionMasseData({...actionMasseData, action: value});
                    setOpenActionMasseModal(true);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Actions en masse..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="archiver">📦 Archiver</SelectItem>
                    <SelectItem value="supprimer">🗑️ Supprimer</SelectItem>
                    <SelectItem value="suspendre">⏸️ Suspendre</SelectItem>
                    <SelectItem value="reactiver">▶️ Réactiver</SelectItem>
                    <SelectItem value="resilier">🚫 Résilier</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Liste des conventions */}
      <Card>
        <CardContent className="pt-6">
          {filteredConventions.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 font-medium">Aucun convention trouvé</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Checkbox Tout sélectionner */}
              <div className="flex items-center gap-3 pb-3 border-b">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <Label className="font-semibold cursor-pointer" onClick={handleSelectAll}>
                  Tout sélectionner ({filteredConventions.length})
                </Label>
              </div>
              {filteredConventions.map((convention) => {
                // Couleur de bordure selon le statut
                const getBorderColor = (statut) => {
                  const colors = {
                    brouillon: 'border-gray-300',
                    en_attente_signature: 'border-orange-400',
                    signe: 'border-blue-400',
                    actif: 'border-green-400',
                    suspendu: 'border-red-400',
                    resilie: 'border-red-600',
                    expire: 'border-gray-400'
                  };
                  return colors[statut] || 'border-gray-300';
                };
                
                return (
                  <Card key={convention.id} className={`border-2 ${getBorderColor(convention.statut)} hover:shadow-lg transition-all`}>
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        {/* Checkbox de sélection */}
                        <input
                          type="checkbox"
                          checked={selectedConventions.includes(convention.id)}
                          onChange={() => handleSelectConvention(convention.id)}
                          className="h-5 w-5 mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        
                        <div className="flex-1 flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {getTypeIcon(convention.type_convention)}
                            <h3 className="font-bold text-lg">{convention.titre}</h3>
                            {getStatutBadge(convention.statut)}
                          </div>
                          
                          {/* Badges des dates clés */}
                          <div className="flex flex-wrap gap-2 mt-3 mb-3">
                            {/* Date de création */}
                            <Badge className="bg-slate-700 text-white border-slate-800 font-semibold">
                              <Calendar className="h-3 w-3 mr-1" />
                              Créé: {new Date(convention.created_at).toLocaleDateString('fr-FR', { 
                                day: '2-digit', 
                                month: '2-digit', 
                                year: 'numeric' 
                              })}
                            </Badge>
                            
                            {/* Date de signature */}
                            {convention.date_signature && (
                              <Badge className="bg-green-700 text-white border-green-800 font-semibold">
                                <FileSignature className="h-3 w-3 mr-1" />
                                Signé: {new Date(convention.date_signature).toLocaleDateString('fr-FR', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric' 
                                })}
                              </Badge>
                            )}
                            
                            {/* Date d'expiration */}
                            {convention.date_fin && (
                              <Badge className="bg-amber-700 text-white border-amber-800 font-semibold">
                                <Clock className="h-3 w-3 mr-1" />
                                Expire: {new Date(convention.date_fin).toLocaleDateString('fr-FR', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: 'numeric' 
                                })}
                              </Badge>
                            )}
                          </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-slate-700">📋 N°:</span>
                            <span className="font-mono text-slate-600">{convention.numero_contrat}</span>
                          </div>
                          
                          {convention.destinataire_nom && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-semibold text-slate-700">👤 Destinataire:</span>
                              <span className="text-slate-600">{convention.destinataire_nom}</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-slate-700">📅 Durée:</span>
                            <span className="text-slate-600">{convention.duree_mois} mois</span>
                          </div>

                          {/* Statut avec badge plus visible */}
                          <div className="bg-slate-50 rounded-lg p-3 mt-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-semibold text-slate-600 uppercase">Statut actuel</span>
                              {getStatutBadge(convention.statut)}
                            </div>
                            
                            {/* Dates importantes selon le statut */}
                            <div className="space-y-1 text-xs text-slate-600">
                              {convention.date_envoi && (
                                <div className="flex items-center gap-2">
                                  <span className="text-blue-600">📤</span>
                                  <span>Envoyé le {new Date(convention.date_envoi).toLocaleDateString('fr-FR', { 
                                    day: '2-digit', 
                                    month: 'long', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}</span>
                                </div>
                              )}
                              
                              {convention.date_signature && (
                                <div className="flex items-center gap-2 font-semibold text-green-700">
                                  <span>✍️</span>
                                  <span>Signé le {new Date(convention.date_signature).toLocaleDateString('fr-FR', { 
                                    day: '2-digit', 
                                    month: 'long', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}</span>
                                </div>
                              )}
                              
                              {convention.date_debut && (
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600">🟢</span>
                                  <span>Actif depuis le {new Date(convention.date_debut).toLocaleDateString('fr-FR')}</span>
                                </div>
                              )}
                              
                              {convention.date_fin && (
                                <div className="flex items-center gap-2">
                                  <span className="text-orange-600">⏰</span>
                                  <span>Expire le {new Date(convention.date_fin).toLocaleDateString('fr-FR')}</span>
                                </div>
                              )}
                              
                              {convention.date_suspendre && (
                                <div className="flex items-center gap-2 text-red-600 font-medium">
                                  <span>⏸️</span>
                                  <span>Suspendu le {new Date(convention.date_suspendre).toLocaleDateString('fr-FR')}</span>
                                </div>
                              )}
                              
                              {convention.date_resilier && (
                                <div className="flex items-center gap-2 text-red-700 font-medium">
                                  <span>🚫</span>
                                  <span>Résilié le {new Date(convention.date_resilier).toLocaleDateString('fr-FR')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <HistoriquePartages convention={convention} />
                        </div>
                      </div>

                      <div className="flex gap-2 flex-wrap justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedContrat(convention);
                            setOpenDetailModal(true);
                          }}
                          data-testid={`convention-view-btn-${convention.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>

                        {/* Bouton Dupliquer - disponible pour toutes les conventions */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="border-purple-600 text-purple-600 hover:bg-purple-50"
                          onClick={() => handleDuplicateConvention(convention.id)}
                          data-testid={`convention-duplicate-btn-${convention.id}`}
                        >
                          <Copy className="h-4 w-4 mr-1" />
                          Dupliquer
                        </Button>

                        {convention.statut === 'brouillon' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700"
                              onClick={() => {
                                setSelectedContrat(convention);
                                setOpenEnvoiModal(true);
                              }}
                            >
                              <Send className="h-4 w-4 mr-1" />
                              Envoyer
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-600 text-red-600"
                              onClick={() => handleDelete(convention.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}

                        {/* Bouton téléchargement pour conventions signées */}
                        {(convention.statut === 'signe' || convention.statut === 'actif') && (
                          <ConventionDownload convention={convention} variant="compact" />
                        )}

                        {/* Bouton Paiement/Partage - disponible pour conventions en attente ou actives */}
                        {(convention.statut === 'en_attente_signature' || convention.statut === 'signe' || convention.statut === 'actif') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-purple-600 text-purple-600 hover:bg-purple-50"
                            onClick={() => {
                              setSelectedContrat(convention);
                              setOpenPaymentShareModal(true);
                            }}
                            data-testid={`convention-share-btn-${convention.id}`}
                          >
                            {(convention.montant_mensuel || convention.montant_total) > 0 ? (
                              <>
                                <CreditCard className="h-4 w-4 mr-1" />
                                {convention.payment_status === 'paid' ? 'Partager' : 'Payer'}
                              </>
                            ) : (
                              <>
                                <Share2 className="h-4 w-4 mr-1" />
                                Partager
                              </>
                            )}
                          </Button>
                        )}

                        {convention.statut === 'actif' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-600 text-red-600"
                            onClick={() => {
                              setSelectedContrat(convention);
                              setActionData({ action: 'suspendre', raison: '' });
                              setOpenActionModal(true);
                            }}
                            data-testid={`convention-suspend-btn-${convention.id}`}
                          >
                            <PauseCircle className="h-4 w-4 mr-1" />
                            Suspendre
                          </Button>
                        )}

                        {convention.statut === 'suspendu' && (
                          <>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => {
                                setSelectedContrat(convention);
                                setActionData({ action: 'reactiver', raison: '' });
                                setOpenActionModal(true);
                              }}
                            >
                              <PlayCircle className="h-4 w-4 mr-1" />
                              Réactiver
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-red-800 text-red-800"
                              onClick={() => {
                                setSelectedContrat(convention);
                                setActionData({ action: 'resilier', raison: '' });
                                setOpenActionModal(true);
                              }}
                            >
                              <Ban className="h-4 w-4 mr-1" />
                              Résilier
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Modals
        openCreateModal={openCreateModal} setOpenCreateModal={setOpenCreateModal}
        openEnvoiModal={openEnvoiModal} setOpenEnvoiModal={setOpenEnvoiModal}
        openDetailModal={openDetailModal} setOpenDetailModal={setOpenDetailModal}
        openEditModal={openEditModal} setOpenEditModal={setOpenEditModal}
        openActionModal={openActionModal} setOpenActionModal={setOpenActionModal}
        openActionMasseModal={openActionMasseModal} setOpenActionMasseModal={setOpenActionMasseModal}
        openPaymentShareModal={openPaymentShareModal} setOpenPaymentShareModal={setOpenPaymentShareModal}
        formData={formData} setFormData={setFormData}
        envoiData={envoiData} setEnvoiData={setEnvoiData}
        selectedConvention={selectedConvention} setSelectedContrat={setSelectedContrat}
        actionData={actionData} setActionData={setActionData}
        actionMasseData={actionMasseData} setActionMasseData={setActionMasseData}
        coopersList={coopersList} expertsList={expertsList}
        selectedConventions={selectedConventions}
        isEditing={isEditing} setIsEditing={setIsEditing}
        editFormData={editFormData} setEditFormData={setEditFormData}
        showCreateRecipient={showCreateRecipient} setShowCreateRecipient={setShowCreateRecipient}
        newRecipientEmail={newRecipientEmail} setNewRecipientEmail={setNewRecipientEmail}
        newRecipientPrenom={newRecipientPrenom} setNewRecipientPrenom={setNewRecipientPrenom}
        newRecipientNom={newRecipientNom} setNewRecipientNom={setNewRecipientNom}
        newRecipientEntreprise={newRecipientEntreprise} setNewRecipientEntreprise={setNewRecipientEntreprise}
        creatingRecipient={creatingRecipient}
        handleCreateConvention={handleCreateConvention}
        handleCreateRecipientByEmail={handleCreateRecipientByEmail}
        handleEnvoyerConvention={handleEnvoyerConvention}
        handleAction={handleAction}
        handleActionMasse={handleActionMasse}
        handleOpenEdit={handleOpenEdit}
        handleSaveEdit={handleSaveEdit}
        handleCancelEdit={handleCancelEdit}
        resetActionMasseData={resetActionMasseData}
        fetchData={fetchData}
        getAuthHeader={getAuthHeader}
        getStatutBadge={getStatutBadge}
        getTypeIcon={getTypeIcon}
      />
    </div>
  );
}
